import React, { useState, useEffect } from 'react';
import { useCartStore } from '../store/cartStore';
import ProductSearch from '../components/ProductSearch';
import Cart from '../components/Cart';
import CheckoutModal from '../components/CheckoutModal';
import api from '../utils/api';

function POS() {
  const { cart, fetchCart } = useCartStore();
  const [showCheckout, setShowCheckout] = useState(false);
  const [pincode, setPincode] = useState('');
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [pincodeResults, setPincodeResults] = useState(null);
  const [pincodeLocation, setPincodeLocation] = useState(null);
  const [inventoryRefreshKey, setInventoryRefreshKey] = useState(0);
  const [selectedLocation, setSelectedLocation] = useState(
    localStorage.getItem('selectedLocation') || ''
  );

  useEffect(() => {
    fetchCart();
    const onLocationChange = (e) => {
      const newLocationId = e.detail;
      setSelectedLocation(newLocationId);
    };
    window.addEventListener('locationchange', onLocationChange);
    return () => window.removeEventListener('locationchange', onLocationChange);
  }, [fetchCart]);

  const handleCheckout = () => {
    if (cart.items.length === 0) {
      alert('Cart is empty');
      return;
    }
    if (!selectedLocation) {
      alert('Please select a location from the dropdown at the top of the page');
      return;
    }
    setShowCheckout(true);
  };

  const handleCheckoutComplete = (order) => {
    setShowCheckout(false);
    if (order.multiOrder && order.orders) {
      const orderList = order.orders.map(o => `${o.name || '#' + o.orderNumber} (${o.locationName || 'Location'})`).join(', ');
      alert(`${order.orders.length} orders created successfully!\n${orderList}`);
    } else {
      alert(`Order ${order.name || '#' + order.orderNumber} created successfully!`);
    }
    fetchCart();
    setInventoryRefreshKey(k => k + 1);
    setPincode('');
    setPincodeResults(null);
    setPincodeLocation(null);
  };

  const handleCheckPincode = async () => {
    if (!pincode || pincode.length !== 6) {
      alert('Please enter a valid 6-digit pincode');
      return;
    }
    if (cart.items.length === 0) return;
    setPincodeLoading(true);
    setPincodeResults(null);
    try {
      const items = cart.items.filter(item => (item.locationType || 'store') === 'warehouse').map(item => {
        const attrs = item.customAttributes || [];
        const customSizeAttr = attrs.find(a => a.key === '_custom_size' || a.key === 'custom_size');
        const customSizeVal = (customSizeAttr?.value || '');
        const isMto = customSizeVal.includes('_ismto');
        const isCustomSize = !!customSizeAttr && !isMto;
        return {
          sku: item.sku,
          title: item.title,
          isCustom: isCustomSize,
          isMto,
          isCustomSize
        };
      });
      if (items.length === 0) {
        setPincodeResults({ success: true, results: [] });
        setPincodeLoading(false);
        return;
      }
      const res = await api.post('/checkout/verify-pincode', { pincode, items });
      setPincodeResults(res.data);
    } catch (e) {
      setPincodeResults({ success: false, results: [{ deliverable: false, error: 'Failed to check delivery' }] });
    }
    setPincodeLoading(false);
  };

  return (
    <div style={{ maxWidth: '1600px', margin: '0 auto', position: 'relative' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 400px',
        gap: '20px',
        height: 'calc(100vh - 120px)'
      }}>
        {/* Left side - Product Search */}
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 8px 32px rgba(26, 26, 46, 0.06)',
          border: '1px solid #ece0d1',
          overflow: 'auto'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px', color: '#3d2b1f', letterSpacing: '-0.02em' }}>
            Products
          </h2>
          <ProductSearch locationId={selectedLocation} refreshKey={inventoryRefreshKey} />
        </div>

        {/* Right side - Cart */}
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(26, 26, 46, 0.06)',
          border: '1px solid #ece0d1',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{ flex: 1, overflow: 'auto' }}>
            <Cart />
          </div>
          
          <div style={{
            padding: '20px',
            borderTop: '1px solid rgba(200, 131, 70, 0.12)'
          }}>
            {/* Pincode Checker — only show when warehouse items are in cart */}
            {cart.items.length > 0 && cart.items.some(item => (item.locationType || 'store') === 'warehouse') && (
              <div style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    maxLength={6}
                    value={pincode}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, '');
                      setPincode(val);
                      setPincodeResults(null);
                      if (val.length === 6) {
                        api.get(`/checkout/pincode-lookup/${val}`)
                          .then(resp => {
                            if (resp.data?.success && resp.data.city) {
                              setPincodeLocation({ city: resp.data.city, state: resp.data.state });
                            } else { setPincodeLocation(null); }
                          }).catch(() => setPincodeLocation(null));
                      } else { setPincodeLocation(null); }
                    }}
                    placeholder="Enter pincode"
                    style={{
                      flex: 1, padding: '10px 12px', border: '2px solid rgba(200, 131, 70, 0.15)',
                      borderRadius: '10px', fontSize: '14px', fontWeight: '600',
                      outline: 'none', letterSpacing: '1.5px', boxSizing: 'border-box',
                      background: '#ffffff'
                    }}
                    onFocus={e => e.target.style.borderColor = '#6366f1'}
                    onBlur={e => e.target.style.borderColor = 'rgba(200, 131, 70, 0.15)'}
                    onKeyDown={e => { if (e.key === 'Enter') handleCheckPincode(); }}
                  />
                  <button
                    onClick={handleCheckPincode}
                    disabled={pincodeLoading || pincode.length !== 6}
                    style={{
                      padding: '10px 16px', background: pincode.length === 6 ? '#6366f1' : '#d1d5db',
                      color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px',
                      fontWeight: '700', cursor: pincode.length === 6 ? 'pointer' : 'not-allowed',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {pincodeLoading ? '...' : 'Check'}
                  </button>
                </div>

                {pincodeLocation && pincode.length === 6 && (
                  <div style={{ fontSize: '11px', color: '#7a6b5d', marginBottom: '6px', paddingLeft: '2px' }}>
                    📍 {pincodeLocation.city}, {pincodeLocation.state}
                  </div>
                )}

                {pincodeResults && pincodeResults.results && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {pincodeResults.results.map((r, idx) => (
                      <div key={idx} style={{
                        padding: '8px 12px', borderRadius: '10px', fontSize: '12px', fontWeight: '600',
                        background: r.deliverable ? '#eaf7ee' : '#fdeaea',
                        color: r.deliverable ? '#166534' : '#991b1b',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px'
                      }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                          {r.title || r.sku}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                          {r.isMto ? <span style={{ color: '#fff', background: '#7c3aed', fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px' }}>MTO</span> : r.isCustomSize ? <span style={{ color: '#fff', background: '#c88346', fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px' }}>Custom</span> : null}
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {r.deliverable ? (
                              <>{r.deliveryDate ? `✓ ${new Date(r.deliveryDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : '✓ Serviceable'}</>
                            ) : (
                              <>✗ {r.error || 'Not serviceable'}</>
                            )}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={handleCheckout}
              disabled={cart.items.length === 0}
              style={{
                width: '100%',
                padding: '14px',
                background: cart.items.length === 0 ? 'rgba(200, 131, 70, 0.3)' : 'linear-gradient(135deg, #c88346 0%, #b06e2f 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                fontSize: '15px',
                fontWeight: '700',
                cursor: cart.items.length === 0 ? 'not-allowed' : 'pointer',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: cart.items.length === 0 ? 'none' : '0 4px 14px rgba(200, 131, 70, 0.3)',
                letterSpacing: '-0.01em'
              }}
            >
              Checkout ({cart.items.length} items)
            </button>
          </div>
        </div>
      </div>

      {showCheckout && (
        <CheckoutModal
          onClose={() => setShowCheckout(false)}
          onComplete={handleCheckoutComplete}
          locationId={selectedLocation}
        />
      )}
    </div>
  );
}

export default POS;
